<template>
  <div class="top-nav-strip">
    <nav class="top-nav-strip__nav" role="navigation" aria-label="Main navigation">
      <slot>
        <a
          v-for="(item, index) in items"
          :key="index"
          class="top-nav-strip__item"
          :class="{ 'top-nav-strip__item--active': item.active }"
          :href="item.href || item.to || '#'"
          :aria-current="item.active ? 'page' : undefined"
          @click="handleItemClick(item, $event)"
          @keydown.enter="handleItemClick(item, $event)"
          @keydown.space.prevent="handleItemClick(item, $event)"
        >
          <lucide-icon :name="item.icon || 'Circle'" :size="16" class="mr-1" aria-hidden="true" />
          {{ item.label }}
        </a>
      </slot>
    </nav>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import LucideIcon from '../LucideIcon';

export interface TopNavItem {
  label: string;
  icon?: string;
  active?: boolean;
  to?: string;
  href?: string;
  action?: string;
}

export default Vue.extend({
  name: 'TopNavStrip',

  components: {
    LucideIcon
  },

  props: {
    items: {
      type: Array as () => TopNavItem[],
      default: () => [],
      validator: (value: TopNavItem[]) => {
        return Array.isArray(value) && value.every(item =>
          typeof item.label === 'string' && item.label.length > 0
        );
      }
    }
  },

  methods: {
    handleItemClick(item: TopNavItem, event?: Event) {
      // Prevent default if using # fallback
      if ((!item.href && !item.to) || (item.href === '#' || item.to === '#')) {
        event?.preventDefault();
      }
      this.$emit('item-click', item);
      if (item.action) {
        this.$emit(item.action);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/spacing.scss';

.top-nav-strip {
  background-color: #ffffff;
  background-color: var(--card, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  border-bottom-color: var(--border, #e5e7eb);
  padding: 0 $spacing-xl;

  &__nav {
    display: flex;
    gap: $spacing-xs;
    align-items: center;
  }

  &__item {
    display: inline-flex;
    align-items: center;
    padding: $spacing-md $spacing-lg;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
    text-decoration: none;
    border-radius: 0;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    border-bottom: 2px solid transparent;

    ::v-deep .v-icon {
      color: inherit;
      opacity: 0.7;
    }

    &:hover {
      color: #374151;
      color: var(--secondary-foreground, #374151);
      background-color: transparent;
    }

    &:focus-visible {
      outline: 2px solid #406A94;
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 2px;
      border-radius: $spacing-xs;
    }

    &--active {
      color: #374151;
      color: var(--secondary-foreground, #374151);
      font-weight: 500;
      border-bottom-color: #406A94;
      border-bottom-color: var(--primary, #406A94);
      background-color: transparent;

      ::v-deep .v-icon {
        opacity: 1;
      }
    }
  }
}

// Dark mode overrides
:global(.dark) {
  .top-nav-strip {
    background-color: var(--card);
    border-bottom-color: var(--border);
  }
}
</style>
